import apiService from './api';
import type { ApiResponse } from '../types';
import type {
  Equipment,
  EquipmentFormData,
  EquipmentTypeOption,
  ManagedEquipmentType,
  EquipmentTypeInput,
  EquipmentHealthSummary,
  EquipmentAttachment,
  MaintenanceInterval,
  MaintenanceIntervalFormData,
  UsageSession,
  CompleteSessionData,
  ChecklistTemplate,
  ChecklistTemplatePayload,
  EquipmentServiceType,
  ServiceRecord,
  ServiceRecordPartLine,
  ServiceRecordPartLineInput,
  EquipmentCategory,
  EquipmentAssignment,
  AssignEquipmentPayload,
  HeldEquipment,
  EquipmentRuntime,
  EquipmentPhoto,
} from '../types/equipment';
import type { CompatiblePart } from '../types/parts';
import { downscaleImage } from '../utils/downscaleImage';

// ─── Paginated response — adds page metadata to the ApiResponse success branch
type ApiPaged<T> =
  | { success: true;  data: T[]; total: number; page: number; totalPages: number; message?: string }
  | { success: false; error: { message: string; code?: string; details?: Record<string, unknown> }; message?: string };

/**
 * The paginated equipment endpoints spread their service result into the
 * response (`res.json({ success: true, ...data })`), so the rows arrive under a
 * domain name — `sessions`, `records`, `history` — never under `data`. A caller
 * reading `res.data` therefore gets `undefined` and renders an empty page while
 * the request itself succeeded: the Usage History, Service Records and
 * attachment-history pages were all blank for this reason, regardless of what
 * the database held.
 *
 * Normalise here rather than at each call site, so `ApiPaged<T>.data` means what
 * it says. `data` is still preferred when present, in case a route is ever
 * tidied up to send it.
 */
function pageOf<T>(raw: unknown, key: string): ApiPaged<T> {
  const res = (raw ?? {}) as Record<string, unknown>;
  if (!res.success) return res as unknown as ApiPaged<T>;

  // The rows sit at the top level (`{ success, sessions, total }` — what the
  // routes actually send) or, if a caller ever wraps the body once more, one
  // level down under `data`. Look in both rather than pick one.
  const nested = res.data && typeof res.data === 'object' && !Array.isArray(res.data)
    ? res.data as Record<string, unknown>
    : null;
  const rows = (
    Array.isArray(res.data) ? res.data : (nested?.[key] ?? res[key])
  ) as T[] | undefined;

  const num = (k: string, fallback: number) => {
    const v = nested?.[k] ?? res[k];
    return typeof v === 'number' ? v : fallback;
  };

  const data = Array.isArray(rows) ? rows : [];
  return {
    success: true,
    data,
    total: num('total', data.length),
    page: num('page', 1),
    totalPages: num('totalPages', 1),
  };
}

// ============================================================================
// EQUIPMENT SERVICE
// ============================================================================

class EquipmentService {

  // ─── Types ────────────────────────────────────────────────────────────────

  async getTypes(): Promise<ApiResponse<EquipmentTypeOption[]>> {
    return apiService.get('/equipment/types');
  }

  async listManagedTypes(): Promise<ApiResponse<ManagedEquipmentType[]>> {
    return apiService.get('/equipment/types/manage');
  }

  async createType(data: EquipmentTypeInput): Promise<ApiResponse<EquipmentTypeOption>> {
    return apiService.post('/equipment/types', data);
  }

  async updateType(typeKey: string, data: Partial<EquipmentTypeInput>): Promise<ApiResponse<EquipmentTypeOption>> {
    return apiService.put(`/equipment/types/${encodeURIComponent(typeKey)}`, data);
  }

  /** A type in use needs `reassignTo` — its machines and checklists move there first. */
  async deleteType(typeKey: string, reassignTo?: string): Promise<ApiResponse<{ deleted: string; moved: { machines: number; templates: number } }>> {
    const q = reassignTo ? `?reassign_to=${encodeURIComponent(reassignTo)}` : '';
    return apiService.delete(`/equipment/types/${encodeURIComponent(typeKey)}${q}`);
  }

  // ─── Equipment CRUD ───────────────────────────────────────────────────────

  async list(filters?: {
    status?: string; type?: string; farm_id?: number; is_implement?: boolean;
    category?: EquipmentCategory; assigned_to?: number;
  }): Promise<ApiResponse<Equipment[]>> {
    const params = new URLSearchParams();
    if (filters?.status !== undefined)      params.set('status',       filters.status);
    if (filters?.type !== undefined)        params.set('type',         filters.type);
    if (filters?.farm_id !== undefined)     params.set('farm_id',      String(filters.farm_id));
    if (filters?.is_implement !== undefined) params.set('is_implement', String(filters.is_implement));
    if (filters?.category !== undefined)    params.set('category',     filters.category);
    if (filters?.assigned_to !== undefined) params.set('assigned_to',  String(filters.assigned_to));
    const qs = params.toString();
    return apiService.get(`/equipment${qs ? `?${qs}` : ''}`);
  }

  async getById(id: number): Promise<ApiResponse<Equipment>> {
    return apiService.get(`/equipment/${id}`);
  }

  async getActive(): Promise<ApiResponse<Equipment[]>> {
    return apiService.get('/equipment/active');
  }

  async getHealthSummary(): Promise<ApiResponse<EquipmentHealthSummary>> {
    return apiService.get('/equipment/health-summary');
  }

  async create(data: EquipmentFormData): Promise<ApiResponse<Equipment>> {
    return apiService.post('/equipment', data);
  }

  async update(id: number, data: Partial<EquipmentFormData>): Promise<ApiResponse<Equipment>> {
    return apiService.put(`/equipment/${id}`, data);
  }

  // ─── Custody ──────────────────────────────────────────────────────────────

  /** What the signed-in person is holding. Needs no equipment seat. */
  async getMine(): Promise<ApiResponse<HeldEquipment[]>> {
    return apiService.get('/equipment/mine');
  }

  async getAssignments(equipmentId: number): Promise<ApiResponse<EquipmentAssignment[]>> {
    return apiService.get(`/equipment/${equipmentId}/assignments`);
  }

  /** Issue the machine — or hand it over, if somebody else holds it. */
  async assign(equipmentId: number, data: AssignEquipmentPayload): Promise<ApiResponse<EquipmentAssignment>> {
    return apiService.post(`/equipment/${equipmentId}/assignments`, data);
  }

  async returnAssignment(assignmentId: number, returnNotes?: string): Promise<ApiResponse<EquipmentAssignment>> {
    return apiService.post(`/equipment/assignments/${assignmentId}/return`, { return_notes: returnNotes || null });
  }

  /** The irrigation node a pump is, and its relay's recent runs. */
  async getRuntime(equipmentId: number): Promise<ApiResponse<EquipmentRuntime>> {
    return apiService.get(`/equipment/${equipmentId}/runtime`);
  }

  async retire(id: number): Promise<ApiResponse<{ id: number; status: string }>> {
    return apiService.delete(`/equipment/${id}`);
  }

  // ─── Photos ───────────────────────────────────────────────────────────────

  async getPhotos(equipmentId: number): Promise<ApiResponse<EquipmentPhoto[]>> {
    return apiService.get(`/equipment/${equipmentId}/photos`);
  }

  /** Scaled down on the device first, so a paddock connection is not asked to carry 8 MB. */
  async uploadPhoto(equipmentId: number, file: File, opts?: { caption?: string; is_primary?: boolean }): Promise<ApiResponse<EquipmentPhoto>> {
    const upload = await downscaleImage(file);
    const form = new FormData();
    form.append('photo', upload, upload.name);
    if (opts?.caption) form.append('caption', opts.caption);
    if (opts?.is_primary) form.append('is_primary', 'true');
    return apiService.post(`/equipment/${equipmentId}/photos`, form, undefined, { timeout: 120000 });
  }

  async setPrimaryPhoto(photoId: number): Promise<ApiResponse<EquipmentPhoto>> {
    return apiService.put(`/equipment/photos/${photoId}`, { is_primary: true });
  }

  async updatePhotoCaption(photoId: number, caption: string): Promise<ApiResponse<EquipmentPhoto>> {
    return apiService.put(`/equipment/photos/${photoId}`, { caption });
  }

  async deletePhoto(photoId: number): Promise<ApiResponse<{ id: number; deleted: boolean }>> {
    return apiService.delete(`/equipment/photos/${photoId}`);
  }

  /** An object URL for the photo; the file endpoint needs the bearer token, so an <img src> cannot fetch it. */
  async fetchPhotoUrl(photoId: number): Promise<string> {
    const blob = await (apiService.get(`/equipment/photos/${photoId}/file`, { responseType: 'blob', timeout: 60000 }) as unknown as Promise<Blob>);
    return URL.createObjectURL(blob);
  }

  // ─── Attachments ──────────────────────────────────────────────────────────

  async getAttachments(primeMoverIdOrId: number): Promise<ApiResponse<EquipmentAttachment[]>> {
    return apiService.get(`/equipment/${primeMoverIdOrId}/attachments`);
  }

  async attach(primeMoverIdOrId: number, implementId: number, notes?: string): Promise<ApiResponse<EquipmentAttachment>> {
    return apiService.post(`/equipment/${primeMoverIdOrId}/attachments`, { implement_id: implementId, notes });
  }

  async detach(attachmentId: number): Promise<ApiResponse<EquipmentAttachment>> {
    return apiService.delete(`/equipment/attachments/${attachmentId}`);
  }

  async getAttachmentHistory(equipmentId: number, page = 1, limit = 20): Promise<ApiPaged<EquipmentAttachment>> {
    return pageOf<EquipmentAttachment>(
      await apiService.get(`/equipment/${equipmentId}/attachment-history?page=${page}&limit=${limit}`),
      'history',
    );
  }

  // ─── Maintenance Intervals ────────────────────────────────────────────────

  async getMaintenanceIntervals(equipmentId: number): Promise<ApiResponse<MaintenanceInterval[]>> {
    return apiService.get(`/equipment/${equipmentId}/maintenance`);
  }

  async getDueMaintenanceItems(equipmentId: number): Promise<ApiResponse<MaintenanceInterval[]>> {
    return apiService.get(`/equipment/${equipmentId}/maintenance/due`);
  }

  async createMaintenanceInterval(equipmentId: number, data: MaintenanceIntervalFormData): Promise<ApiResponse<MaintenanceInterval>> {
    return apiService.post(`/equipment/${equipmentId}/maintenance`, data);
  }

  async updateMaintenanceInterval(intervalId: number, data: Partial<MaintenanceIntervalFormData>): Promise<ApiResponse<MaintenanceInterval>> {
    return apiService.put(`/equipment/maintenance/${intervalId}`, data);
  }

  async deleteMaintenanceInterval(intervalId: number): Promise<ApiResponse<{ id: number; deleted: boolean }>> {
    return apiService.delete(`/equipment/maintenance/${intervalId}`);
  }

  async completeMaintenanceInterval(intervalId: number): Promise<ApiResponse<MaintenanceInterval>> {
    return apiService.post(`/equipment/maintenance/${intervalId}/complete`);
  }

  // ─── Usage Sessions ───────────────────────────────────────────────────────

  async getSessions(equipmentId: number, page = 1, limit = 20): Promise<ApiPaged<UsageSession>> {
    return pageOf<UsageSession>(
      await apiService.get(`/equipment/${equipmentId}/sessions?page=${page}&limit=${limit}`),
      'sessions',
    );
  }

  async getActiveSession(equipmentId: number): Promise<ApiResponse<UsageSession | null>> {
    return apiService.get(`/equipment/${equipmentId}/sessions/active`);
  }

  async getSessionById(sessionId: number): Promise<ApiResponse<UsageSession>> {
    return apiService.get(`/equipment/sessions/${sessionId}`);
  }

  async completeSession(sessionId: number, data: CompleteSessionData): Promise<ApiResponse<UsageSession>> {
    return apiService.post(`/equipment/sessions/${sessionId}/complete`, data);
  }

  async cancelSession(sessionId: number): Promise<ApiResponse<UsageSession>> {
    return apiService.post(`/equipment/sessions/${sessionId}/cancel`, {});
  }

  // GPS for equipment runs through the unified job waypoint pipeline
  // (`jobService.postWaypoints` → `location_records`). The per-session GPS
  // endpoints were removed; `equipment_gps_tracks` is kept for historical
  // read-only data only.

  // ─── Checklists ───────────────────────────────────────────────────────────

  async getChecklistTemplates(equipmentId: number): Promise<ApiResponse<ChecklistTemplate[]>> {
    return apiService.get(`/equipment/${equipmentId}/checklists`);
  }

  async recordChecklistCompletion(
    equipmentId: number,
    data: {
      template_id: number;
      usage_session_id?: number | null;
      spray_application_id?: number | null;
      notes?: string;
      items: { template_item_id: number; passed: boolean; notes?: string }[];
      maintenance_items?: { interval_id: number; passed: boolean; notes?: string }[];
    }
  ): Promise<ApiResponse<unknown>> {
    return apiService.post(`/equipment/${equipmentId}/checklist-completions`, data);
  }

  // ─── Checklist Template Admin ─────────────────────────────────────────────

  async listChecklistTemplates(equipmentType?: string): Promise<ApiResponse<ChecklistTemplate[]>> {
    const qs = equipmentType ? `?equipment_type=${equipmentType}` : '';
    return apiService.get(`/equipment/checklists/templates${qs}`);
  }

  async getChecklistTemplate(id: number): Promise<ApiResponse<ChecklistTemplate>> {
    return apiService.get(`/equipment/checklists/templates/${id}`);
  }

  async createChecklistTemplate(data: ChecklistTemplatePayload): Promise<ApiResponse<ChecklistTemplate>> {
    return apiService.post('/equipment/checklists/templates', data);
  }

  async updateChecklistTemplate(id: number, data: ChecklistTemplatePayload): Promise<ApiResponse<ChecklistTemplate>> {
    return apiService.put(`/equipment/checklists/templates/${id}`, data);
  }

  async deleteChecklistTemplate(id: number): Promise<ApiResponse<{ id: number; deleted: boolean }>> {
    return apiService.delete(`/equipment/checklists/templates/${id}`);
  }

  // ─── Service Types Admin ──────────────────────────────────────────────────

  async listServiceTypes(): Promise<ApiResponse<EquipmentServiceType[]>> {
    return apiService.get('/equipment/service-types');
  }

  async createServiceType(data: { name: string; description?: string }): Promise<ApiResponse<EquipmentServiceType>> {
    return apiService.post('/equipment/service-types', data);
  }

  async updateServiceType(id: number, data: Partial<EquipmentServiceType>): Promise<ApiResponse<EquipmentServiceType>> {
    return apiService.put(`/equipment/service-types/${id}`, data);
  }

  // ─── Service Records ──────────────────────────────────────────────────────

  async getServices(equipmentId: number, page = 1, limit = 20): Promise<ApiPaged<ServiceRecord>> {
    return pageOf<ServiceRecord>(
      await apiService.get(`/equipment/${equipmentId}/services?page=${page}&limit=${limit}`),
      'records',
    );
  }

  async createService(
    equipmentId: number,
    data: { title: string; service_type_id?: number | null; description?: string; scheduled_date?: string; assigned_to?: number | null; hours_at_service?: number | null }
  ): Promise<ApiResponse<ServiceRecord>> {
    return apiService.post(`/equipment/${equipmentId}/services`, data);
  }

  async getServiceById(serviceId: number): Promise<ApiResponse<ServiceRecord>> {
    return apiService.get(`/equipment/services/${serviceId}`);
  }

  async updateService(serviceId: number, data: Partial<ServiceRecord>): Promise<ApiResponse<ServiceRecord>> {
    return apiService.put(`/equipment/services/${serviceId}`, data);
  }

  async completeService(
    serviceId: number,
    data: {
      technician_notes?: string;
      equipment_condition?: string;
      next_service_date?: string;
      next_service_hours?: number;
      labour_hours?: number;
      labour_cost?: number;
    }
  ): Promise<ApiResponse<ServiceRecord>> {
    return apiService.post(`/equipment/services/${serviceId}/complete`, data);
  }

  // ─── Parts ────────────────────────────────────────────────────────────────

  /**
   * List parts compatible with this equipment (direct links + generic
   * make/model/year matches).
   */
  async getEquipmentParts(equipmentId: number): Promise<ApiResponse<CompatiblePart[]>> {
    return apiService.get(`/equipment/${equipmentId}/parts`);
  }

  /**
   * Resolve a VIN or rego to an equipment record for the caller's org.
   */
  async lookup(params: { vin?: string; rego?: string; state?: string }): Promise<ApiResponse<Equipment>> {
    const qs = new URLSearchParams();
    if (params.vin)   qs.set('vin',   params.vin);
    if (params.rego)  qs.set('rego',  params.rego);
    if (params.state) qs.set('state', params.state);
    return apiService.get(`/equipment/lookup?${qs.toString()}`);
  }

  // ─── Service Record Parts (line items) ───────────────────────────────────

  async getServiceRecordParts(equipmentId: number, serviceRecordId: number): Promise<ApiResponse<ServiceRecordPartLine[]>> {
    return apiService.get(`/equipment/${equipmentId}/service-records/${serviceRecordId}/parts`);
  }

  async addServiceRecordParts(
    equipmentId: number,
    serviceRecordId: number,
    lines: ServiceRecordPartLineInput[]
  ): Promise<ApiResponse<{ lines: ServiceRecordPartLine[] }>> {
    return apiService.post(`/equipment/${equipmentId}/service-records/${serviceRecordId}/parts`, { lines });
  }

  async removeServiceRecordPart(
    equipmentId: number,
    serviceRecordId: number,
    lineId: number
  ): Promise<ApiResponse<{ id: number }>> {
    return apiService.delete(`/equipment/${equipmentId}/service-records/${serviceRecordId}/parts/${lineId}`);
  }
}

export const equipmentService = new EquipmentService();
export default equipmentService;
